import type { RecordingIntent } from "../api/client.ts";
import type { components } from "../api/schema.ts";
import { ControlError, type Recording, type Session } from "../api/parse.ts";
import type { SessionLaunch } from "../session-document.ts";
import {
  admitCad,
  cadBusyMs,
  cadHintLine,
  cadHintMs,
  cadNotSentLine,
  cadNotVisibleLine,
  cadPendingLine,
  cadRecordFailedLine,
  cadShownLine,
  cadWindowMs,
  type CadGate,
  type CadResultError,
} from "./cad.ts";
import { ICE_RECONNECTING_LABEL } from "./iceLink.ts";
import { clipboardInLine, flushHeldClipboard, holdLocalClipboard, imageFileToPng, type LocalPaste } from "./localClipboard.ts";
import { mediaGrant, idlePhase, reduce, screenOf, screensEqual, type DeskEvent, type FileProgress, type Phase, type Screen, type SessionView } from "./model.ts";
import { visibleFailure } from "../stepUp.ts";
import { sessionCreateBody } from "../consentTrail.ts";
import { openBrowserMedia, type LiveMedia } from "./transport.ts";
import type { ViewerLink } from "./viewerStats.ts";

type CadRequestedEvent = components["schemas"]["CadRequestedEvent"];

type OpenMedia = typeof openBrowserMedia;

export type DeskApi = {
  createSession(body: {
    deviceId: string;
    accessMode: Session["accessMode"];
    features: { clipboard: boolean; fileTransfer: boolean; recording: boolean };
    reason?: string;
  }): Promise<Session>;
  getSession(sessionId: string): Promise<Session>;
  endSession(sessionId: string, body: { disconnectReason: "OperatorEnded" }, options?: { keepalive?: boolean }): Promise<Session>;
  listRecordings(sessionId: string): Promise<Recording[]>;
  recordingContent(recordingId: string, intent: RecordingIntent): Promise<Blob>;
  reportSessionEvent(sessionId: string, body: CadRequestedEvent): Promise<void>;
};

export type Desk = {
  screen(): Screen;
  accessMode(): "Attended" | "Unattended" | null;
  clipboardText(): string;
  clipboardNotice(): string | null;
  clipboardChannelOpen(): boolean;
  filesChannelOpen(): boolean;
  cadGate(): CadGate;
  cadNotice(): string | null;
  linkNotice(): string | null;
  sessionId(): string | null;
  subscribe(listener: () => void): () => void;
  start(input: SessionLaunch, reason?: string): Promise<void>;
  selectDisplay(displayId: string): Promise<void>;
  setView(view: SessionView): void;
  end(): Promise<void>;
  sendClipboard(text: string): Promise<void>;
  pasteToDevice(payload: LocalPaste): Promise<void>;
  flushClipboard(): void;
  sendFile(file: File): Promise<void>;
  secureAttention(): Promise<void>;
  pointer(input: { x: number; y: number; buttons: number; action: "move" | "down" | "up" }): void;
  key(input: { action: "down" | "up"; code: string; modifiers: Array<"Control" | "Alt" | "Shift" | "Meta"> }): void;
  wheel(input: { deltaX: number; deltaY: number }): void;
  attach(video: HTMLVideoElement | null): void;
  readLink(): Promise<ViewerLink | null>;
};

export function createDesk(input: {
  api: DeskApi;
  apiBase?: string;
  openMedia?: OpenMedia;
  now?: () => number;
  schedule?: (fn: () => void, delayMs: number) => () => void;
  saveFile?: (name: string, bytes: Uint8Array) => void;
  who?: () => string | null;
  newId?: () => string;
}): Desk {
  const open = input.openMedia ?? ((session, hooks) => openBrowserMedia(session, hooks, input.apiBase ?? ""));
  const now = input.now ?? Date.now;
  const who = input.who ?? (() => null);
  const newId = input.newId ?? (() => crypto.randomUUID());
  const schedule = input.schedule ?? ((fn, delayMs) => {
    const timer = setTimeout(fn, delayMs);
    return () => clearTimeout(timer);
  });
  let phase: Phase = idlePhase;
  let launchMode: "Attended" | "Unattended" | null = null;
  let screenSnapshot: Screen = { kind: "idle" };
  let media: LiveMedia | null = null;
  let opening = false;
  let mediaTicket = 0;
  let cancelOpen: (() => void) | null = null;
  let clipboardText = "";
  let clipboardNotice: string | null = null;
  let controlOpen = false;
  let clipboardChannelOpen = false;
  let filesChannelOpen = false;
  let cadSentAt: number[] = [];
  let cadHoldUntil = 0;
  let cadNotice: string | null = null;
  let linkNotice: string | null = null;
  let cadBusy = false;
  let lastCadRequestId: string | null = null;
  let cadOutcome: "none" | "ok" | CadResultError = "none";
  let cadTimer: (() => void) | null = null;
  let cadHintStop: (() => void) | null = null;
  let cadBusyStop: (() => void) | null = null;
  let pollStop: (() => void) | null = null;
  let chain: Promise<void> = Promise.resolve();
  let sessionKey: string | null = null;
  let endRequested = false;
  let finishedEnd = false;
  let endPromise: Promise<Session> | null = null;
  const listeners = new Set<() => void>();

  function emit() {
    for (const listener of listeners) listener();
  }

  function noteClipboard(result: string) {
    if (result === "closed") clipboardChannelOpen = false;
    clipboardNotice =
      result === "sent"
        ? "Clipboard sent"
        : result === "closed"
          ? "Clipboard waits for an open channel."
          : result === "disabled"
            ? "Clipboard is off for this session."
            : "Clipboard was refused";
    emit();
  }

  function clearCadTimer() {
    cadTimer?.();
    cadTimer = null;
  }

  function clearCadWait() {
    cadHintStop?.();
    cadHintStop = null;
    cadBusyStop?.();
    cadBusyStop = null;
  }

  function resetCad() {
    clearCadTimer();
    clearCadWait();
    controlOpen = false;
    clipboardChannelOpen = false;
    filesChannelOpen = false;
    cadSentAt = [];
    cadHoldUntil = 0;
    cadNotice = null;
    cadBusy = false;
    lastCadRequestId = null;
    cadOutcome = "none";
  }

  function refreshCadWindow() {
    clearCadTimer();
    const at = now();
    cadSentAt = cadSentAt.filter((sentAt) => at - sentAt < cadWindowMs);
    if (cadHoldUntil <= at) cadHoldUntil = 0;
    const limited = cadHoldUntil > at || !admitCad(cadSentAt, at);
    if (!limited) return;
    const oldest = cadSentAt.length > 0 ? Math.min(...cadSentAt) + cadWindowMs : at;
    const wake = Math.max(oldest, cadHoldUntil);
    cadTimer = schedule(() => {
      cadTimer = null;
      refreshCadWindow();
      emit();
    }, Math.max(0, wake - at));
  }

  function cadGate(): CadGate {
    if (phase.kind !== "live" || !media || !controlOpen) return "channel";
    if (cadBusy) return "sending";
    const at = now();
    if (at < cadHoldUntil || !admitCad(cadSentAt, at)) return "limited";
    return "ready";
  }

  function armCadWait(requestId: string) {
    clearCadWait();
    cadHintStop = schedule(() => {
      cadHintStop = null;
      if (lastCadRequestId !== requestId || cadOutcome !== "none") return;
      cadNotice = cadHintLine;
      emit();
    }, cadHintMs);
    cadBusyStop = schedule(() => {
      cadBusyStop = null;
      if (lastCadRequestId !== requestId || !cadBusy) return;
      cadBusy = false;
      emit();
    }, cadBusyMs);
  }

  function applyCadOutcome() {
    if (cadOutcome === "ok") {
      cadNotice = cadShownLine(new Date(now()));
      return;
    }
    if (cadOutcome === "rate_limited") {
      cadHoldUntil = now() + cadWindowMs;
      refreshCadWindow();
      cadNotice = null;
      return;
    }
    if (cadOutcome === "not_connected") {
      cadNotice = "The device is not connected for Ctrl+Alt+Del.";
      return;
    }
    if (cadOutcome === "not_visible") {
      cadNotice = cadNotVisibleLine;
      return;
    }
    if (cadOutcome === "not_sent" || cadOutcome === "sas_failed") {
      cadNotice = cadNotSentLine;
    }
  }

  function enqueue(task: () => Promise<void>): Promise<void> {
    chain = chain.then(task, task);
    return chain;
  }

  function sessionId(): string | null {
    if (phase.kind === "awaiting" || phase.kind === "connecting" || phase.kind === "live" || (phase.kind === "ended" && phase.session)) {
      return phase.kind === "ended" ? phase.session?.sessionId ?? null : phase.session.sessionId;
    }
    return null;
  }

  async function apply(event: DeskEvent) {
    const next = reduce(phase, event);
    phase = next.phase;
    emit();
    if (event.type === "create") resetCad();
    if (next.effects.some((effect) => effect.type === "close-transport")) {
      mediaTicket += 1;
      cancelOpen?.();
      cancelOpen = null;
      media?.close();
      media = null;
      opening = false;
      controlOpen = false;
      clipboardChannelOpen = false;
      filesChannelOpen = false;
      linkNotice = null;
      clearCadTimer();
      clearCadWait();
      stopPoll();
    }
    if (next.effects.some((effect) => effect.type === "poll")) armPoll();
    if (!endRequested && next.effects.some((effect) => effect.type === "open-transport") && phase.kind === "connecting" && !opening && !media) {
      const ticket = mediaTicket;
      const session = phase.session;
      opening = true;
      void open(session, {
        onCloseable(close) {
          if (ticket === mediaTicket) cancelOpen = close;
        },
        onDisplays(displays, selectedDisplayId) {
          void enqueue(async () => {
            if (ticket !== mediaTicket || phase.kind !== "live") return;
            await apply({ type: "displays", displays });
            if (selectedDisplayId) await apply({ type: "display-selected", displayId: selectedDisplayId });
          });
        },
        onClipboard(text) {
          clipboardText = text;
          holdLocalClipboard({ text });
          emit();
        },
        onClipboardPng(bytes) {
          holdLocalClipboard({ png: bytes });
        },
        onFile(file: FileProgress | null) {
          void enqueue(() => apply({ type: "file", file }));
        },
        onSaveFile(name, bytes) {
          input.saveFile?.(name, bytes);
        },
        onFailed(message) {
          void enqueue(async () => {
            if (ticket !== mediaTicket) return;
            await apply({ type: "fail", message });
          });
        },
        onLink(status) {
          if (ticket !== mediaTicket) return;
          const next = status === "reconnecting" ? ICE_RECONNECTING_LABEL : null;
          if (next === linkNotice) return;
          linkNotice = next;
          emit();
        },
        onControlOpen(open) {
          if (ticket !== mediaTicket) return;
          controlOpen = open;
          emit();
        },
        onClipboardOpen(open) {
          if (ticket !== mediaTicket) return;
          clipboardChannelOpen = open;
          emit();
        },
        onFilesOpen(open) {
          if (ticket !== mediaTicket) return;
          filesChannelOpen = open;
          emit();
        },
        onCadResult(result) {
          if (ticket !== mediaTicket || result.requestId !== lastCadRequestId || cadOutcome !== "none") return;
          cadOutcome = result.ok ? "ok" : result.error ?? "sas_failed";
          cadBusy = false;
          clearCadWait();
          applyCadOutcome();
          emit();
        },
      }, input.apiBase ?? "")
        .then((live) =>
          enqueue(async () => {
            if (ticket !== mediaTicket) {
              live.close();
              return;
            }
            media = live;
            opening = false;
            cancelOpen = null;
            await apply({ type: "transport-ready", displays: live.displays, selectedDisplayId: live.selectedDisplayId });
          }),
        )
        .catch((error: unknown) =>
          enqueue(async () => {
            if (ticket !== mediaTicket) return;
            opening = false;
            const message = visibleFailure(error, "Media failed");
            if (message) await apply({ type: "fail", message });
          }),
        );
    }
  }

  function stopPoll() {
    pollStop?.();
    pollStop = null;
  }

  function armPoll() {
    if (pollStop) return;
    const current = sessionId();
    if (!current) return;
    pollStop = schedule(() => {
      pollStop = null;
      void enqueue(async () => {
        if ((phase.kind !== "awaiting" && phase.kind !== "connecting") || phase.session.sessionId !== current) return;
        try {
          const session = await input.api.getSession(current);
          await apply({ type: "refreshed", session });
        } catch (error) {
          const message = visibleFailure(error, "Session refresh failed");
          if (message) await apply({ type: "fail", message });
        }
      });
    }, 1000);
  }

  // useSyncExternalStore compares this with Object.is. A fresh object on every read is an infinite update.
  function screen(): Screen {
    const next = screenOf(phase, now());
    if (!screensEqual(screenSnapshot, next)) screenSnapshot = next;
    return screenSnapshot;
  }

  function issueEnd(id: string): Promise<Session> {
    if (!endPromise) {
      endPromise = input.api.endSession(id, { disconnectReason: "OperatorEnded" }, { keepalive: true });
    }
    return endPromise;
  }

  async function settleEnd(): Promise<void> {
    if (finishedEnd) {
      if (phase.kind !== "ended") await apply({ type: "ended", reason: "OperatorEnded" });
      return;
    }
    const pending = endPromise;
    if (!sessionKey || !pending) return;
    try {
      await pending;
      finishedEnd = true;
      if (phase.kind !== "ended") await apply({ type: "ended", reason: "OperatorEnded" });
    } catch (error) {
      if (endPromise === pending) endPromise = null;
      if (phase.kind !== "failed") {
        const message = visibleFailure(error, "Could not end the session");
        if (message) await apply({ type: "fail", message });
      }
    }
  }

  return {
    screen,
    accessMode: () => launchMode,
    clipboardText: () => clipboardText,
    clipboardNotice: () => clipboardNotice,
    clipboardChannelOpen: () => clipboardChannelOpen,
    filesChannelOpen: () => filesChannelOpen,
    cadGate,
    cadNotice: () => cadNotice,
    linkNotice: () => linkNotice,
    sessionId: () => sessionKey,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    start(request, reason) {
      launchMode = request.accessMode;
      return enqueue(async () => {
        await apply({ type: "create" });
        try {
          const session = await input.api.createSession(sessionCreateBody(request, reason));
          sessionKey = session.sessionId;
          if (endRequested) {
            const pending = issueEnd(session.sessionId);
            pending.catch(() => undefined);
            await settleEnd();
            return;
          }
          await apply({ type: "created", session });
        } catch (error) {
          const message = visibleFailure(error, "Could not start the session");
          if (message) await apply({ type: "fail", message });
        }
      });
    },
    selectDisplay(displayId) {
      return enqueue(async () => {
        await media?.selectDisplay(displayId);
        await apply({ type: "display-selected", displayId });
      });
    },
    setView(view) {
      void enqueue(() => apply({ type: "set-view", view }));
    },
    end() {
      if (finishedEnd) return Promise.resolve();
      endRequested = true;
      mediaTicket += 1;
      cancelOpen?.();
      cancelOpen = null;
      media?.close();
      media = null;
      opening = false;
      stopPoll();
      const id = sessionKey ?? sessionId();
      if (id) {
        sessionKey = id;
        const pending = issueEnd(id);
        pending.catch(() => undefined);
      }
      return enqueue(() => settleEnd());
    },
    async sendClipboard(text) {
      if (!media) {
        clipboardNotice = "Clipboard is available once the session is live";
        emit();
        return;
      }
      noteClipboard(media.sendClipboard(text));
    },
    async sendFile(file) {
      if (!media) return;
      const bytes = new Uint8Array(await file.arrayBuffer());
      await media.sendFile(bytes, file.name);
    },
    flushClipboard() {
      flushHeldClipboard();
    },
    async pasteToDevice(payload) {
      if (!media) {
        if (payload.kind !== "file") {
          const bytes = payload.kind === "text" ? new TextEncoder().encode(payload.text).length : payload.file.size;
          console.info(clipboardInLine({ mime: payload.kind === "text" ? "text/plain" : "image/png", bytes, result: "closed" }));
          noteClipboard("closed");
        }
        return;
      }
      if (payload.kind === "text") {
        if (payload.text.length === 0) return;
        const result = media.sendClipboard(payload.text);
        console.info(clipboardInLine({ mime: "text/plain", bytes: new TextEncoder().encode(payload.text).length, result }));
        noteClipboard(result);
        return;
      }
      const bytes = new Uint8Array(await payload.file.arrayBuffer());
      if (payload.kind === "png") {
        const result = media.sendPng(bytes);
        console.info(clipboardInLine({ mime: "image/png", bytes: bytes.byteLength, result }));
        noteClipboard(result);
        return;
      }
      if (payload.kind === "image") {
        const png = await imageFileToPng(payload.file);
        if (!png) {
          console.info(clipboardInLine({ mime: "image/png", bytes: payload.file.size, result: "refused" }));
          noteClipboard("refused");
          return;
        }
        const result = media.sendPng(png);
        console.info(clipboardInLine({ mime: "image/png", bytes: png.byteLength, result }));
        noteClipboard(result);
        return;
      }
      await media.sendFile(bytes, payload.file.name);
    },
    pointer(pointer) {
      media?.sendPointer(pointer);
    },
    key(key) {
      media?.sendKey(key);
    },
    wheel(wheel) {
      media?.sendWheel(wheel);
    },
    async secureAttention() {
      if (phase.kind !== "live" || !media || mediaGrant(phase.session) === null) {
        cadNotice = "Ctrl+Alt+Del is available on a connected session.";
        emit();
        return;
      }
      if (cadGate() !== "ready") {
        emit();
        return;
      }
      const identity = who();
      if (!identity) {
        cadNotice = "Sign in before sending Ctrl+Alt+Del.";
        emit();
        return;
      }
      const session = phase.session;
      const live = media;
      cadBusy = true;
      cadNotice = cadPendingLine;
      cadOutcome = "none";
      clearCadWait();
      emit();
      const requestId = newId();
      lastCadRequestId = requestId;
      const ts = new Date(now()).toISOString();
      try {
        await input.api.reportSessionEvent(session.sessionId, {
          type: "cad.requested",
          who: identity,
          when: ts,
          deviceId: session.deviceId,
          sessionId: session.sessionId,
          accessMode: session.accessMode,
          requestId,
        });
      } catch (error) {
        cadBusy = false;
        if (error instanceof ControlError && error.problemType === "urn:wylo-rc:error:limit-exceeded") {
          cadHoldUntil = now() + cadWindowMs;
          refreshCadWindow();
        }
        cadNotice = cadRecordFailedLine;
        emit();
        return;
      }
      if (phase.kind !== "live" || media !== live || lastCadRequestId !== requestId) {
        cadBusy = false;
        emit();
        return;
      }
      const sent = live.sendCad({ requestId, ts });
      if (!sent.ok) {
        cadBusy = false;
        if (sent.reason === "not_open") controlOpen = false;
        cadNotice = sent.reason === "not_open" ? "Ctrl+Alt+Del waits for an open control channel." : "Ctrl+Alt+Del was refused.";
        emit();
        return;
      }
      cadSentAt.push(now());
      refreshCadWindow();
      armCadWait(requestId);
      emit();
    },
    readLink() {
      return media?.readLink?.() ?? Promise.resolve(null);
    },
    attach(video) {
      if (video) media?.attach(video);
    },
  };
}
